
import { Component, Vue } from 'vue-property-decorator';
import { GeocodeResult } from '@googlemaps/google-maps-services-js';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import GoogleMaps from '../../anonymous/GoogleMaps.vue';

import SelectField from '../../../../framework/fields/SelectField.vue';
import TextField from '../../../../framework/fields/TextField.vue';
import SwitchField from '../../../../framework/fields/SwitchField.vue';

import { sharedState } from '../../../../framework/state';
import { postResource } from '../../../../framework/client/resource';
import { getEnumerationOptions } from '../../../../framework/service/options';

import { VendorType } from '../../../../../common/application/model/enums/VendorType';
import { Vendor } from '../../../../../common/application/model/Vendor';
import { VendorLocation } from '../../../../../common/application/model/VendorLocation';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { successToast, errorToast } from '../../../service/toast_service';
import { parseGeocodeResult } from '../../../../../common/application/service/google_maps_service';
import { InvoicePeriodizationType } from '../../../../../common/application/model/enums/InvoicePeriodizationType';
import PeriodizationTypeSelector from '../../shared/PeriodizationTypeSelector.vue';
import { InvoicePeriodizationOffsetDirection } from '../../../../../common/application/model/enums/InvoicePeriodizationOffsetDirection';
import { computed } from 'vue';
import { PeriodizationModel } from '../../../../../common/application/model/PeriodizationModel';

@Component({
    components: {
        SwitchField,
        SelectField,
        TextField,
        GoogleMaps,
        ValidationObserver,
        ValidationProvider,
        PeriodizationTypeSelector,
    },
})
export default class AddVendor extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR;

    // Properties

    // State
    shared = sharedState;
    local = {
        row: {
            euVendor: false,
            parsePurchaseInvoicesWithTextract: false,
            invoicePeriodizationType: InvoicePeriodizationType.UNSPECIFIED,
            invoicePeriodizationUseOffset: false,
            invoicePeriodizationOffsetDirection: InvoicePeriodizationOffsetDirection.ADD,
            invoicePeriodizationOffsetDays: 0,
        } as Vendor,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    get periodizationModel(): PeriodizationModel {
        return {
            type: this.local.row.invoicePeriodizationType,
            useOffset: this.local.row.invoicePeriodizationUseOffset ?? false,
            direction: this.local.row.invoicePeriodizationOffsetDirection ?? InvoicePeriodizationOffsetDirection.ADD,
            days: this.local.row.invoicePeriodizationOffsetDays ?? 0,
        };
    }

    onPeriodizationModelChanged(newModel: PeriodizationModel) {
        this.local.row = {
            ...this.local.row,
            invoicePeriodizationType: newModel.type,
            invoicePeriodizationUseOffset: newModel.useOffset,
            invoicePeriodizationOffsetDirection: newModel.direction,
            invoicePeriodizationOffsetDays: newModel.days,
        };
    }

    validatePeriodization(value: any) {
        if (!value || !value.type || value.type.toLowerCase() === 'unspecified') {
            return this.$t('message.invoicePeriodizationTypeRequired');
        }
        if (value.useOffset) {
            if (!value.direction) {
                return this.$t('message.offsetDirectionRequired');
            }
            if (!value.days || isNaN(value.days) || Number(value.days) <= 0) {
                return this.$t('message.offsetDaysRequired');
            }
        }
        return true;
    }

    // Functions
    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
    }

    async add() {
        await this.addResource();
    }

    async addAndClose() {
        await this.addResource(true);
    }

    private async addResource(goBack?: boolean) {
        try {
            const newVendor = await postResource<Vendor>(this.resourceType, this.local.row);

            await postResource<VendorLocation>(ApplicationResource.VENDOR_LOCATION, {
                id: '',
                created: new Date(),
                modified: new Date(),
                parentId: newVendor.id,
                name: newVendor.name,
                vendorType: newVendor.vendorType,
                street: newVendor.vendorAddressStreet,
                city: newVendor.vendorAddressCity,
                zip: newVendor.vendorAddressZip,
                country: newVendor.vendorAddressCountry,
                email: '',
                phone: '',
                url: '',
                lat: newVendor.lat,
                lng: newVendor.lng,
            });

            successToast(this, 'message.vendorSaved');

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    get fullAddress(): string {
        if (!this.local.row.vendorAddressStreet) {
            return '';
        }

        return `${this.local.row.vendorAddressStreet}, ${this.local.row.vendorAddressZip} ${this.local.row.vendorAddressCity}`;
    }

    setAddress(geocodeResult: GeocodeResult) {
        const parsedLocation = parseGeocodeResult(geocodeResult);

        this.local.row = {
            ...this.local.row,
            vendorAddressStreet: parsedLocation.street,
            vendorAddressZip: parsedLocation.zip,
            vendorAddressCity: parsedLocation.city,
            vendorAddressCountry: parsedLocation.country,
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }
}
